import React from "react";
import { useParams } from "react-router-dom";
import { apiGetSpk, apiGetPengaturan, formatApiErrorDetail } from "@/lib/api";

// Thermal SPK, one per machine (requirement 5.4). Paper width comes from
// Pengaturan (58 or 80 mm). No prices: an SPK is a production document.
const BAYAR_LABEL = { BELUM_BAYAR: "BELUM BAYAR", DP: "DP", LUNAS: "LUNAS" };
const TTD = ["Designer", "Kasir", "Operator", "Finishing", "QC", "Customer"];

const fmt = (iso, tz) =>
  new Intl.DateTimeFormat("id-ID", { timeZone: tz, dateStyle: "short", timeStyle: "short" }).format(iso ? new Date(iso) : new Date());

export default function CetakSpk() {
  const { id } = useParams();
  const [spk, setSpk] = React.useState(null);
  const [cfg, setCfg] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    Promise.all([apiGetSpk(id), apiGetPengaturan()])
      .then(([s, p]) => { setSpk(s); setCfg(p); })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [id]);

  if (error) return <div data-testid="cetak-error" className="p-6 text-red-600">{error}</div>;
  if (!spk || !cfg) return <div className="p-6 text-slate-500">Memuat…</div>;

  if (spk.jersey_snapshot) return <SpkJersey spk={spk} cfg={cfg} />;
  const width = cfg.lebar_thermal_mm;
  const tz = cfg.zona_waktu;
  const items = spk.items_snapshot || [];
  const totalFile = items.filter((it) => it.nama_file).length;
  const row = (label, value, testid) => (
    <div className="flex gap-1"><span className="shrink-0 whitespace-nowrap" style={{ width: "30%" }}>{label}</span><span>:</span><span data-testid={testid} className="break-words">{value || "-"}</span></div>
  );

  return (
    <div className="min-h-screen bg-slate-200 print:bg-white py-6 print:py-0">
      <style>{`@page { size: ${width}mm auto; margin: 0; } @media print { .no-print { display: none !important; } }`}</style>
      <div className="no-print mx-auto mb-4 flex justify-center gap-3" style={{ maxWidth: "24rem" }}>
        <button data-testid="cetak-print" onClick={() => window.print()} className="rounded bg-soq-biru px-4 py-2 text-sm text-white">Cetak</button>
        <span className="self-center text-xs text-slate-600">Kertas {width} mm (ubah di Pengaturan)</span>
      </div>
      {!cfg.nama_usaha && <p className="no-print text-center text-xs text-amber-700 mb-2">Nama usaha belum diisi di Pengaturan.</p>}
      <div data-testid="cetak-paper" data-width={width} className="mx-auto bg-white text-black font-mono leading-snug p-[3mm]"
        style={{ width: `${width}mm`, fontSize: width === 58 ? "9pt" : "10.5pt" }}>
        <div className="text-center font-bold" data-testid="cetak-usaha">{cfg.nama_usaha}</div>
        <div className="text-center font-bold border-y border-black border-dashed my-1 py-1" data-testid="cetak-mesin">
          MESIN : {(spk.jenis_mesin_nama || "").toUpperCase()}
        </div>
        {row("Faktur", spk.nomor_faktur, "cetak-faktur")}
        {row("SPK", spk.nomor_spk, "cetak-spk")}
        {row("Bayar", BAYAR_LABEL[spk.status_bayar] || spk.status_bayar, "cetak-bayar")}
        {row("Tanggal", fmt(spk.created_at, tz))}
        {row("Customer", spk.customer_snapshot?.nama, "cetak-customer")}
        {row("No WA", spk.customer_snapshot?.nomor_telepon)}
        {row("Designer", spk.designer?.nama, "cetak-designer")}
        {row("File", spk.jenis_file)}
        <div className="border-t border-black border-dashed my-1" />
        {items.map((it, i) => (
          <div key={i} data-testid={`cetak-item-${i}`} className="mb-1">
            <div className="font-bold">{it.nama_barang || it.produk}</div>
            <div data-testid={`cetak-item-qty-${i}`}>{it.panjang_nyata_cm ? `panjang ${(it.panjang_nyata_cm / 100).toLocaleString("id-ID")} m` : it.p_cm ? `${it.quantity} x ${it.p_cm / 100}x${it.l_cm / 100} m` : `${it.quantity} ${it.satuan}`}</div>
            {it.finishing && <div>Fin: {it.finishing}</div>}
            {it.nama_file && <div className="break-all">File: {it.nama_file}</div>}
          </div>
        ))}
        <div className="border-t border-black border-dashed my-1" />
        {row("Jml item", items.length, "cetak-total-item")}
        {row("Jml file", totalFile, "cetak-total-file")}
        {row("Status", spk.status_spk)}
        {spk.catatan_snapshot && row("Catatan", spk.catatan_snapshot)}
        <div className="grid grid-cols-2 gap-x-2 gap-y-5 mt-3 text-center" style={{ fontSize: "0.85em" }}>
          {TTD.map((t) => (
            <div key={t}><div className="border-b border-black h-6" /><div>{t}</div></div>
          ))}
        </div>
        <div className="text-center mt-3" style={{ fontSize: "0.8em" }} data-testid="cetak-waktu">Dicetak {fmt(null, tz)}</div>
      </div>
    </div>
  );
}

// Jersey SPK on A4 [ASUMSI F1-F]: identity, variasi, size recap, the full
// name & number list with a QC column, signatures per production post.
// No prices and no PIC contact.
const TTD_JERSEY = ["Desain", "Pattern & Layout", "Sublim", "Press", "Jahit", "QC"];

function SpkJersey({ spk, cfg }) {
  const j = spk.jersey_snapshot;
  const tz = cfg.zona_waktu;
  const variasi = (spk.items_snapshot || []).filter((it) => it.jenis_baris === "VARIASI");
  const tambahan = (spk.items_snapshot || []).filter((it) => it.jenis_baris === "TAMBAHAN");
  const cell = "border border-black px-1.5 py-0.5";
  return (
    <div className="min-h-screen bg-slate-200 print:bg-white py-6 print:py-0">
      <style>{`@page { size: A4; margin: 10mm; } @media print { .no-print { display: none !important; } }`}</style>
      <div className="no-print mx-auto mb-4 flex justify-center gap-3">
        <button data-testid="cetak-print" onClick={() => window.print()} className="rounded bg-soq-biru px-4 py-2 text-sm text-white">Cetak (A4)</button>
      </div>
      <div data-testid="cetak-jersey" className="mx-auto bg-white text-black p-[8mm] text-[10pt] leading-snug" style={{ width: "210mm" }}>
        <div className="flex justify-between items-start border-b-2 border-black pb-2 mb-2">
          <div><div className="text-lg font-bold">{cfg.nama_usaha || "SPK"}</div><div className="text-base font-bold">SPK JERSEY</div></div>
          <div className="text-right font-mono">
            <div data-testid="cetak-faktur">{spk.nomor_faktur}</div><div data-testid="cetak-spk">{spk.nomor_spk}</div>
            {j.revisi_ke > 0 && <div data-testid="cetak-revisi" className="font-bold">REVISI {j.revisi_ke}</div>}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-6 mb-2">
          <div>Customer: <b>{spk.customer_snapshot?.nama}</b></div><div>Tim/komunitas: <b>{j.tim_komunitas || "-"}</b></div>
          <div>Designer: {spk.designer?.nama}</div><div>{j.cara_serah === "KIRIM" ? "Kirim" : "Ambil"}: <b>{j.tanggal_ambil_kirim || "-"}</b></div>
          <div>Tanggal SPK: {fmt(spk.created_at, tz)}</div><div>Bayar: {BAYAR_LABEL[spk.status_bayar] || spk.status_bayar}</div>
        </div>
        <table className="w-full border-collapse mb-2 text-[9pt]">
          <thead><tr>{["#", "Barang", "Jenis", "Model", "Kerah", "Bahan", "Warna", "Motif", "Cutting", "Ukuran", "Qty"].map((h) => <th key={h} className={cell}>{h}</th>)}</tr></thead>
          <tbody>{variasi.map((v) => (
            <tr key={v.baris_no}><td className={cell}>{v.baris_no}</td><td className={cell}>{v.nama_barang}</td><td className={cell}>{v.jenis_jersey}</td><td className={cell}>{v.model}</td><td className={cell}>{v.kerah}</td>
              <td className={cell}>{v.bahan}</td><td className={cell}>{v.warna}</td><td className={cell}>{v.motif}</td><td className={cell}>{v.cutting}</td><td className={cell}>{v.ukuran_ringkas}</td><td className={`${cell} text-right`}>{v.quantity}</td></tr>
          ))}</tbody>
        </table>
        {tambahan.length > 0 && <div className="mb-2">Tambahan: {tambahan.map((t) => `${t.nama_barang} × ${t.quantity}${t.catatan_baris ? ` (${t.catatan_baris})` : ""}`).join(" · ")}</div>}
        <table className="border-collapse mb-2" data-testid="cetak-ukuran">
          <tbody><tr>{Object.keys(j.ukuran).map((u) => <th key={u} className={cell}>{u}</th>)}<th className={cell}>TOTAL</th></tr>
            <tr>{Object.values(j.ukuran).map((n, i) => <td key={i} className={`${cell} text-center`}>{n || ""}</td>)}<td className={`${cell} text-center font-bold`}>{j.total_pcs}</td></tr></tbody>
        </table>
        {j.ukuran_custom_catatan && <div className="mb-2">Ukuran custom: {j.ukuran_custom_catatan}</div>}
        <table className="w-full border-collapse text-[9pt]" data-testid="cetak-pemain">
          <thead><tr>{["No", "Nama", "Nomor", "Ukuran", "Lengan", "Catatan", "QC"].map((h) => <th key={h} className={cell}>{h}</th>)}</tr></thead>
          <tbody>{j.pemain.map((p, i) => (
            <tr key={i}><td className={cell}>{i + 1}</td><td className={cell}>{p.nama}</td><td className={`${cell} text-center font-bold`}>{p.nomor}</td><td className={`${cell} text-center`}>{p.ukuran}</td>
              <td className={cell}>{p.lengan === "PANJANG" ? "Panjang" : "Pendek"}</td><td className={cell}>{p.catatan}</td><td className={`${cell} w-10 text-center`}>{p.qc_ok ? "✓" : ""}</td></tr>
          ))}</tbody>
        </table>
        {spk.catatan_snapshot && <div className="mt-2">Catatan: {spk.catatan_snapshot}</div>}
        <div className="grid grid-cols-6 gap-3 mt-6 text-center text-[9pt]">
          {TTD_JERSEY.map((t) => <div key={t}><div className="border-b border-black h-10" /><div>{t}</div></div>)}
        </div>
        <div className="text-right mt-3 text-[8pt]">Dicetak {fmt(null, tz)} · status SPK {spk.status_spk}</div>
      </div>
    </div>
  );
}
